import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import LegalFooter from "../components/LegalFooter";
import { LEGAL } from "../lib/legal";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-gray-900" data-testid="privacy-page">
      <header className="border-b border-gray-300 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" />
          <Link
            to="/login"
            data-testid="privacy-back-link"
            className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-10 w-full">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-1 mb-2">
          Privacy Policy
        </h1>
        <p className="text-xs text-gray-500 mb-8">
          Effective {LEGAL.effectiveDate} · Last updated {LEGAL.effectiveDate}
        </p>

        <Section title="1. Who we are">
          {LEGAL.company} (the &ldquo;Company&rdquo;, &ldquo;we&rdquo;) operates {LEGAL.productName} (the
          &ldquo;Service&rdquo;). We are bound by the Privacy Act 1988 (Cth) and the
          Australian Privacy Principles in relation to personal information
          collected through the Service.
        </Section>

        <Section title="2. What we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account data:</strong> name, email, role (admin or team member), the
              cryptographic hash of your password and your two security
              question answers.</li>
            <li><strong>Roster & attendance data:</strong> the sites you are assigned to, the
              hours you are rostered, every clock-in and clock-out event with
              its timestamp.</li>
            <li><strong>Geolocation data:</strong> your device&rsquo;s latitude and longitude at
              clock-in / clock-out, and periodic samples (~ every minute)
              while you are clocked in, used to verify you remained on the
              worksite.</li>
            <li><strong>Cleaning compliance data:</strong> records of which tasks you
              completed and at what time.</li>
            <li><strong>Photo uploads:</strong> images you attach to cleaning task
              completions, stored in the Company&rsquo;s secure object storage.</li>
            <li><strong>Technical logs:</strong> standard request logs (IP, user agent,
              timestamps) for security and troubleshooting.</li>
          </ul>
        </Section>

        <Section title="3. Why we collect it">
          Workforce management, payroll, client SLA reporting, fraud and
          time-theft prevention, incident investigation, and meeting legal
          record-keeping obligations.
        </Section>

        <Section title="4. Lawful basis">
          We rely on the necessity of processing for performance of your
          employment or contractor agreement, our legitimate interests in
          operating the Service (preventing fraud, demonstrating compliance
          to clients), and where required, your consent (e.g. enabling
          geolocation on your device).
        </Section>

        <Section title="5. How we store it">
          All data is stored in databases hosted in Australia. Passwords are
          stored only as bcrypt hashes; we cannot read them. Authentication
          uses an <code>HttpOnly</code>, <code>Secure</code>,{" "}
          <code>SameSite=Lax</code> cookie that expires after 12 hours of
          inactivity. Photos are stored in object storage with
          access-controlled signed URLs.
        </Section>

        <Section title="6. Who we share it with">
          We do not sell your personal information. We share:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Attendance and SLA reports with the client whose site you cleaned;</li>
            <li>Information required by law (e.g. ATO, FairWork) or by court order;</li>
            <li>Aggregated, non-identifying analytics with the Company&rsquo;s management.</li>
          </ul>
        </Section>

        <Section title="7. How long we keep it">
          <ul className="list-disc pl-5 space-y-1">
            <li>Roster, attendance and payroll records: <strong>7 years</strong> (FairWork
              minimum).</li>
            <li>Cleaning task completions and photos: <strong>2 years</strong> after the
              client engagement ends.</li>
            <li>Real-time geolocation samples (in-shift movement): <strong>90 days</strong>,
              then deleted. Only the clock-in / clock-out coordinates are
              retained beyond that window.</li>
            <li>Technical access logs: <strong>90 days</strong>.</li>
          </ul>
        </Section>

        <Section title="8. Your rights">
          You can ask us at any time to:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Access a copy of the personal information we hold about you;</li>
            <li>Correct any information that is inaccurate or out of date;</li>
            <li>Delete information that is no longer required (subject to our
              record-keeping obligations);</li>
            <li>Withdraw your consent (note: this may prevent you from using
              the Service for shift work).</li>
          </ul>
          Email{" "}
          <a className="text-[#002FA7] hover:underline" href={`mailto:${LEGAL.contactEmail}`}>
            {LEGAL.contactEmail}
          </a>{" "}with your request. We aim to respond within 30 days.
        </Section>

        <Section title="9. Complaints">
          If you are not satisfied with how we handle your personal
          information you can complain to the Office of the Australian
          Information Commissioner at{" "}
          <a className="text-[#002FA7] hover:underline" href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">
            oaic.gov.au
          </a>.
        </Section>

        <Section title="10. Cookies">
          We use one strictly-necessary cookie (<code>access_token</code>) to
          keep you signed in. We do not use third-party tracking cookies or
          advertising pixels.
        </Section>

        <Section title="11. Children">
          The Service is not directed at people under 16. If you believe a
          child has provided us with personal information, please contact us
          and we will delete it.
        </Section>

        <Section title="12. Changes">
          We may update this Privacy Policy. Material changes will be flagged
          in-app the next time you sign in.
        </Section>

        <Section title="13. Contact">
          Privacy questions or requests:{" "}
          <a className="text-[#002FA7] hover:underline" href={`mailto:${LEGAL.contactEmail}`}>
            {LEGAL.contactEmail}
          </a>{" "}· {LEGAL.supportPhone}.
        </Section>
      </main>

      <LegalFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-black text-lg tracking-tight mb-2">{title}</h2>
      <div className="text-sm text-gray-800 leading-relaxed">{children}</div>
    </section>
  );
}
